import React, { forwardRef, useState, useEffect } from "react";
import { TextField } from "@mui/material";
import { styled } from "@mui/material/styles";

type FirestoreTimestamp = {
  seconds: number;
  nanoseconds: number;
};

type Props = {
  type: "date" | "datetime-local";
  title?: string;
  value?: FirestoreTimestamp | null;
  setTillDate?: (date: FirestoreTimestamp | null) => void;
  setFormattedTillDate?: (date: string) => void;
};


const primaryColor = "#292744";

const CustomTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    backgroundColor: "#f7f8fa",
    "& fieldset": {
      borderColor: theme.palette.grey[300],
    },
    "&:hover fieldset": {
      borderColor: primaryColor,
    },
    "&.Mui-focused fieldset": {
      borderColor: primaryColor,
    },
  },
}));

/* ================== UTILS ================== */

const pad = (n: number) => String(n).padStart(2, "0");

const timestampToInputValue = (
  ts: FirestoreTimestamp,
  type: "date" | "datetime-local"
) => {
  const d = new Date(ts.seconds * 1000);

  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const min = pad(d.getMinutes());

  return type === "date"
    ? `${yyyy}-${mm}-${dd}`
    : `${yyyy}-${mm}-${dd}T${hh}:${min}`;
};

const inputValueToTimestamp = (val: string): FirestoreTimestamp | null => {
  if (!val) return null;
  const d = new Date(val);
  if (isNaN(d.getTime())) return null;

  return {
    seconds: Math.floor(d.getTime() / 1000),
    nanoseconds: 0,
  };
};

const formatForDisplay = (ts: {
  seconds: number;
  nanoseconds: number;
}) => {
  return new Date(ts.seconds * 1000).toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

/* ================== COMPONENT ================== */

const InputTextDate = forwardRef<HTMLInputElement, Props>(
  ({ type, title, value, setTillDate, setFormattedTillDate }, ref) => {
    const [errorMsg, setErrorMsg] = useState("");
    const [inputValue, setInputValue] = useState("");

    /* Firestore → Input */
    useEffect(() => {
      if (value) {
        const formatted = timestampToInputValue(value, type);
        setInputValue(formatted);
      }
    }, [value, type]);

    return (
      <CustomTextField
        label={title}
        type={type}
        fullWidth
        value={inputValue}
        inputRef={ref}
        error={!!errorMsg}
        helperText={errorMsg}
        InputLabelProps={{ shrink: true }}
        onChange={(e) => {
          const val = e.target.value;
          setInputValue(val);
          const ts = inputValueToTimestamp(val);
          // Timestamp (for backend)
          setTillDate?.(ts);
          // Formatted string (for display)
          if (ts) {
            const formatted = formatForDisplay(ts);
            setFormattedTillDate?.(formatted);
          }
        }}

      />
    );
  }
);

export default InputTextDate;
